import { View } from 'react-native';
import React, { useState } from 'react';

import { Button } from '@components/Button';
import { CircledIcon } from '@components/CircledIcon';
import { Input } from '@components/Input';
import { Order } from '@lib/types';
import { TimerInput } from '@components/TimerInput';
import { Typography } from '@components/Typography';
import { logger } from '@lib/logger';
import { makeStyles } from '@providers/Theme';
import { updateOrderStatus } from '@lib/api';
import { useNavigation } from '@hooks';
import { useTranslation } from '@providers/I18n';
import { Modal, ModalContentLayout } from '@components/Modal';
import { ToastType, useToast } from '@providers/Toast';

type AcceptOrderProps = {
  order: Order;
};

const useStyles = makeStyles((theme) => ({
  button: {
    marginLeft: theme.spacing(2),
  },
  commentRoot: {
    marginTop: theme.spacing(3),
  },
  commentInput: {
    backgroundColor: theme.palette.grey[200],
  },
}));

export const AcceptOrder: React.FC<AcceptOrderProps> = ({ order }) => {
  const isFleet = order?.orderMessage?.data?.fleet;
  const navigation = useNavigation();

  const toast = useToast();
  const styles = useStyles();
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [comment, setComment] = useState('');
  const initialTimerValue = isFleet
    ? order?.orderMessage?.data?.expectedCookingDuration
    : order?.orderMessage?.data?.expectedDeliveryDuration;
  const [time, setTime] = useState(initialTimerValue ?? 20);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const orderId = order?.orderMessage?.data?.orderShortCode;
  const toastId = `accept-order-toast-${orderId}`;

  const handleToast = (
    toastID: string,
    status: ToastType,
    title: string,
    body: string,
  ) => {
    return toast.show(
      toastId,
      status,
      <Typography>{title}</Typography>,
      <Typography>{body}</Typography>,
    );
  };

  const acceptOrder = async () => {
    try {
      // close the modal instantly and raise a toast, and update the toast with
      // the submit result
      setIsModalOpen(false);

      handleToast(
        toastId,
        'loading',
        t('orderView.acceptModal.toast.loading.title', 'Accept order'),
        t('orderView.acceptModal.toast.loading.message', 'Accepting order'),
      );
      setIsSubmitting(true);

      const response = await updateOrderStatus({
        requestType: 'SET_ORDER_AS_ACCEPTED',
        time: Date.now(),
        orders: [
          {
            orderUUID: order?.orderMessage?.uuid,
            ...(isFleet
              ? { cookingDuration: time }
              : { deliveryDuration: time }),
          },
        ],
        comment,
      });

      const statusCode = response[0].orderResponse[0].code;
      if (statusCode === 'PAYMENT_FAILED') {
        handleToast(
          toastId,
          'error',
          t('orderView.acceptModal.toast.fail.title', 'Order rejected'),
          t(
            'orderView.acceptModal.toast.fail.message',
            'Order is rejected due to insufficient funds',
          ),
        );
      } else {
        handleToast(
          toastId,
          'success',
          t('orderView.acceptModal.toast.success.title', 'Order accepted'),
          t(
            'orderView.acceptModal.toast.success.message',
            'Order accepted successfully',
          ),
        );
      }
      navigation.navigate('NewOrders');
    } catch (err) {
      logger.error('error for acceptting order', err);
      handleToast(
        toastId,
        'error',
        t('orderView.common.toast.error.title', 'Something went wrong!'),
        t(
          'orderView.common.toast.error.message',
          'Something went wrong while accepting order. Please try again later.',
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Button
        style={styles.button}
        variant="primary"
        onPress={() => setIsModalOpen(true)}>
        {t('orderView.acceptButton', {
          defaultValue: 'Accept',
        })}
      </Button>
      <Modal
        open={isModalOpen}
        onRequestClose={() => setIsModalOpen(false)}
        dismissOnBackdropPress={!isSubmitting}>
        <ModalContentLayout
          icon={<CircledIcon icon="hour-glass" preset="warning" />}
          title={
            isFleet
              ? t('orderView.acceptModal.title.fleet', {
                  defaultValue:
                    'Please set order maximum cooking time to the customer',
                })
              : t('orderView.acceptModal.title.od', {
                  defaultValue:
                    'Please set order maximum delivery time to the customer',
                })
          }
          actions={[
            <Button
              variant="primary"
              onPress={acceptOrder}
              loading={isSubmitting}>
              {t('orderView.acceptModal.acceptButton', {
                defaultValue: 'Accept order',
              })}
            </Button>,
            <Button
              variant="secondary"
              onPress={() => setIsModalOpen(false)}
              loading={isSubmitting}>
              {t('orderView.acceptModal.cancelButton', {
                defaultValue: 'Cancel',
              })}
            </Button>,
          ]}
          content={
            <View>
              <TimerInput min={10} value={time} onChange={setTime} />
              <Input
                style={{ root: styles.commentRoot, input: styles.commentInput }}
                label={t('orderView.acceptModal.comment.label', {
                  defaultValue: 'Add a comment',
                })}
                placeholder={t('orderView.acceptModal.comment.placeholder', {
                  defaultValue: 'Enter your comment...',
                })}
                value={comment}
                onChange={setComment}
                numberOfLines={5}
              />
            </View>
          }
        />
      </Modal>
    </>
  );
};
